<template>
    <div class="legal-ai-file-upload">
        <!-- File Drop Zone -->
        <div 
            class="dropzone-container"
            :class="{ 'dragover': isDragOver, 'uploading': isUploading }"
            @drop="handleDrop"
            @dragover="handleDragOver"
            @dragenter="handleDragEnter"
            @dragleave="handleDragLeave"
            @click="triggerFileInput"
        >
            <input 
                ref="fileInput"
                type="file"
                class="hidden"
                accept=".pdf"
                @change="handleFileSelect"
            >
            
            <div class="dropzone-content">
                <div v-if="!isUploading" class="upload-icon">
                    <inline-svg name="upload" classes="w-12 h-12 text-gray-400 dark:text-gray-500 fill-current"></inline-svg>
                </div>
                <div v-else class="upload-icon">
                    <inline-svg name="loading-button" classes="w-12 h-12 text-blue-500 dark:text-blue-400 fill-current animate-spin"></inline-svg>
                </div>
                
                <div class="upload-text">
                    <h3 v-if="!isUploading" class="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Drop your legal documents here
                    </h3>
                    <h3 v-else class="text-lg font-medium text-blue-600 dark:text-blue-400 mb-2">
                        Processing file...
                    </h3>
                    
                    <p v-if="!isUploading" class="text-sm text-gray-500 dark:text-gray-400">
                        or <span class="text-blue-600 dark:text-blue-400 font-medium cursor-pointer">click to browse</span>
                    </p>
                    <p v-else class="text-sm text-gray-500 dark:text-gray-400">
                        Please wait while we process your document
                    </p>
                </div>
                
                <div class="file-info text-xs text-gray-400 dark:text-gray-500 mt-4">
                    Supported formats: PDF only (Max 10MB)
                </div>
            </div>
        </div>

        <!-- Error Messages -->
        <div v-if="errorMessage" class="error-message mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div class="flex">
                <div class="flex-shrink-0">
                    <inline-svg name="exclamation-triangle" classes="w-5 h-5 text-red-400 fill-current"></inline-svg>
                </div>
                <div class="ml-3">
                    <p class="text-sm text-red-800">{{ errorMessage }}</p>
                </div>
            </div>
        </div>

        <!-- Success Message -->
        <div v-if="successMessage" class="success-message mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <div class="flex">
                <div class="flex-shrink-0">
                    <inline-svg name="check-circle" classes="w-5 h-5 text-green-400 fill-current"></inline-svg>
                </div>
                <div class="ml-3">
                    <p class="text-sm text-green-800">{{ successMessage }}</p>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'FileUpload',
    
    data() {
        return {
            isDragOver: false,
            isUploading: false,
            errorMessage: '',
            successMessage: ''
        }
    },

    mounted() {
        // Component is now focused only on file upload
        // Table management is handled by the parent page
    },

    methods: {
        handleDragOver(e) {
            e.preventDefault();
            this.isDragOver = true;
        },

        handleDragEnter(e) {
            e.preventDefault();
            this.isDragOver = true;
        },

        handleDragLeave(e) {
            e.preventDefault();
            // Only set to false if we're leaving the dropzone container
            if (!e.currentTarget.contains(e.relatedTarget)) {
                this.isDragOver = false;
            }
        },

        handleDrop(e) {
            e.preventDefault();
            this.isDragOver = false;
            
            const files = Array.from(e.dataTransfer.files);
            if (files.length > 0) {
                this.processFile(files[0]);
            }
        },

        triggerFileInput() {
            if (!this.isUploading) {
                this.$refs.fileInput.click();
            }
        },

        handleFileSelect(e) {
            const files = Array.from(e.target.files);
            if (files.length > 0) {
                this.processFile(files[0]);
            }
        },

        async processFile(file) {
            // Clear previous messages
            this.errorMessage = '';
            this.successMessage = '';

            // Validate file
            if (!this.validateFile(file)) {
                return;
            }

            this.isUploading = true;

            try {
                const formData = new FormData();
                formData.append('file', file);
                
                // Add any additional metadata if needed from frontend
                // The backend will add user_id and other server-side metadata
                const clientMetadata = {
                    upload_source: 'web_interface',
                    file_size: file.size,
                    file_type: file.type
                };
                formData.append('client_metadata', JSON.stringify(clientMetadata));

                const response = await axios.post('/lease-ai-files/upload', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                    }
                });

                if (response.data.success) {
                    this.successMessage = response.data.message;
                    // Refresh the files table after successful upload
                    setTimeout(() => {
                        if (this.$parent.$refs.filesTable) {
                            this.$parent.$refs.filesTable.refresh();
                            console.log('Files table refreshed after successful upload');
                        } else {
                            console.warn('Files table ref not available, falling back to page reload');
                            window.location.reload();
                        }
                    }, 1500); // Wait 1.5 seconds to show success message
                } else {
                    this.errorMessage = response.data.message || 'Upload failed';
                }

            } catch (error) {
                console.error('Upload error:', error);
                this.errorMessage = error.response?.data?.message || 'Upload failed. Please try again.';
            } finally {
                this.isUploading = false;
                // Clear file input
                this.$refs.fileInput.value = '';
            }
        },

        validateFile(file) {
            const maxSize = 10 * 1024 * 1024; // 10MB

            if (file.size > maxSize) {
                this.errorMessage = 'File size must be less than 10MB';
                return false;
            }

            if (file.type !== 'application/pdf') {
                this.errorMessage = 'Only PDF files are allowed';
                return false;
            }

            return true;
        },




    }
}
</script>

<style scoped>
.dropzone-container {
    @apply border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer transition-colors duration-200;
    background-color: #ffffff;
}

/* Dark mode dropzone */
.dark .dropzone-container {
    @apply border-gray-600;
    background-color: #101A2F;
}

.dropzone-container.dragover {
    @apply border-blue-400 bg-blue-50;
}

/* Dark mode dragover */
.dark .dropzone-container.dragover {
    @apply border-blue-500;
    background-color: #1a2840;
}

.dropzone-container.uploading {
    @apply cursor-not-allowed bg-gray-50;
}

/* Dark mode uploading */
.dark .dropzone-container.uploading {
    @apply cursor-not-allowed;
    background-color: #101A2F;
}

.dropzone-content {
    @apply flex flex-col items-center;
}

.upload-icon {
    @apply mb-4;
}

.upload-text {
    @apply text-center;
}

.file-info {
    @apply border-t border-gray-200 pt-4;
}

.file-item:hover {
    @apply shadow-md;
}

.status-badge {
    @apply inline-flex items-center;
}
</style>
